import { useAuth0 } from "@auth0/auth0-react";
import { AgencyId, Dtos, Email, FirstName, LastName, UserId, UserType } from "@inrev/common";
import { AppLoading } from "@portal/views/shared/AppLoading";
import * as Sentry from "@sentry/react";
import {
	createContext,
	type FC,
	type ReactNode,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { match } from "ts-pattern";
import { loginRedirectOptions } from "../utils/auth";
import { useRequest } from "../utils/request";

export type AuthenticatedUserProviderProps = {
	children: ReactNode;
};

export type AuthenticatedAdminUser = {
	type: typeof UserType.admin;
	id: UserId;
	roles: string[];
	setupComplete: boolean;
	firstName: FirstName;
	lastName: LastName;
	email: Email;
};

export type AuthenticatedAgentUser = {
	type: typeof UserType.agent;
	id: UserId;
	agencyId: AgencyId;
	agencyName: string;
	role: string;
	setupComplete: boolean;
	firstName: FirstName;
	lastName: LastName;
	email: Email;
};

export type AuthenticatedUser = AuthenticatedAdminUser | AuthenticatedAgentUser;

export type AuthenticatedUserContext = {
	user: AuthenticatedUser;
	isAdmin: boolean;
};

export type AuthenticatedUserDispatchContext = {
	setAuthenticatedUser: (user: AuthenticatedUser) => void;
	loginWithRedirectAndClose: () => void;
};

const defaultAuthenticatedUserContext: AuthenticatedUserContext = {
	user: undefined as unknown as AuthenticatedUser,
	isAdmin: false,
};
const defaultAuthenticatedUserDispatchContext: AuthenticatedUserDispatchContext = {
	setAuthenticatedUser: () => {},
	loginWithRedirectAndClose: () => {},
};

// Testing only
export const AuthenticatedUserContext = createContext<AuthenticatedUserContext>(
	defaultAuthenticatedUserContext,
);
const AuthenticatedUserDispatchContext = createContext<AuthenticatedUserDispatchContext>(
	defaultAuthenticatedUserDispatchContext,
);

export const AuthenticatedUserProvider: FC<AuthenticatedUserProviderProps> = ({ children }) => {
	const { get } = useRequest();
	const [user, setUser] = useState<AuthenticatedUser | undefined>();
	const { isLoading, isAuthenticated, loginWithRedirect, user: auth0User } = useAuth0();

	const loginWithRedirectAndClose = () => {
		Sentry.close(100);
		loginWithRedirect(loginRedirectOptions());
	};

	useEffect(() => {
		if (!isLoading) {
			if (isAuthenticated) {
				if (auth0User !== undefined) {
					get<Dtos.User.Get.Response>(`/v2/users/appData`).then((response) => {
						setUser(
							match(response)
								.with({ type: "agent", setupComplete: true }, (inRevUser) => ({
									...user,
									type: UserType.agent,
									agencyId: inRevUser.agencyId,
									agencyName: inRevUser.agencyName,
									id: inRevUser.id,
									role: inRevUser.roles[0],
									setupComplete: inRevUser.setupComplete,
									firstName: inRevUser.firstName,
									lastName: inRevUser.lastName,
									email: inRevUser.email,
								}))
								.with({ type: "agent" }, (inRevUser) => ({
									...user,
									type: UserType.agent,
									agencyId: inRevUser.agencyId,
									agencyName: inRevUser.agencyName,
									id: inRevUser.id,
									role: inRevUser.roles[0],
									setupComplete: inRevUser.setupComplete,
									firstName: "" as FirstName,
									lastName: "" as LastName,
									email: inRevUser.email,
								}))
								.with({ type: "admin" }, (inRevUser) => ({
									...user,
									type: UserType.admin,
									id: inRevUser.id,
									roles: inRevUser.roles,
									setupComplete: inRevUser.setupComplete,
									firstName: inRevUser.firstName,
									lastName: inRevUser.lastName,
									email: inRevUser.email,
								}))
								.run(),
						);
					});
				}
			} else {
				loginWithRedirectAndClose();
			}
		}
	}, [isLoading]);

	useEffect(() => {
		if (user) {
			Sentry.setUser(user);
		}
	}, [user]);

	// The context value of user will always be defined because of the loading logic below
	const contextValue = useMemo(
		() => ({
			user: user!,
			isAdmin: user?.type === "admin",
		}),
		[user],
	);

	const contextDispatchValue = useMemo(
		() => ({
			setAuthenticatedUser: setUser,
			loginWithRedirectAndClose,
		}),
		[setUser, loginWithRedirectAndClose],
	);

	// While we're loading the Auth0 state, or if the user is not authenticated, or if we haven't
	// yet loaded the user from our backend, show a loading screen. This guarantees we will always
	// have a valid user in the context for any children of this provider
	if (isLoading || !isAuthenticated || user === undefined) {
		return <AppLoading key="2" />;
	}

	return (
		<AuthenticatedUserContext.Provider value={contextValue}>
			<AuthenticatedUserDispatchContext.Provider value={contextDispatchValue}>
				{children}
			</AuthenticatedUserDispatchContext.Provider>
		</AuthenticatedUserContext.Provider>
	);
};

export const useAuthenticatedUser = () => useContext(AuthenticatedUserContext);
export const useAuthenticatedUserDispatch = () => useContext(AuthenticatedUserDispatchContext);
